import {
  Box,
  Button,
  Flex,
  Heading,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { translationAction } from 'actions';
import DeleteModal from 'components/Modal/DeleteModal';
import EditModal from 'components/Modal/EditModal';
import { RouterPrompt } from 'components/RouterPrompt';
import { TEMPORARY } from 'helpers/constant';
import { timeFormat } from 'helpers/date';
import { TRANSLATION_FILE_STATUS, UPLOAD_FILES_STATUS } from 'helpers/enum';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiPlusMedical } from 'react-icons/bi';
import { BsChatLeftDots, BsThreeDotsVertical } from 'react-icons/bs';
import { PiSidebarSimpleBold } from 'react-icons/pi';
import { useDispatch, useSelector } from 'react-redux';
import { Task } from 'types/reducers/task';
import { TranslationTask } from 'types/reducers/translation';

interface TaskListProps {
  onAddNew: () => void;
  onChange: (_task: Task) => void;
  isToggleSidebar: boolean;
  setIsToggleSidebar: React.Dispatch<React.SetStateAction<boolean>>;
}

const TranslationJobs = ({
  onAddNew,
  onChange,
  isToggleSidebar,
  setIsToggleSidebar,
}: TaskListProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { tasks, current } = useSelector((state) => state.translation);
  const { status } = useSelector((state) => state.upload);
  const { data, total_page, current_page } = tasks;

  const [recordDelete, setRecordDelete] = useState<TranslationTask>();
  const [recordEdit, setRecordEdit] = useState<TranslationTask>();
  const [selectTask, setSelectTask] = useState<TranslationTask>();

  const {
    isOpen: isShowWarningChangeTaskDialog,
    onOpen: onOpenWarningChangeTaskDialog,
    onClose: onCloseWarningChangeTaskDialog,
  } = useDisclosure();
  const {
    isOpen: isOpenEditDialog,
    onOpen: onOpenEditDialog,
    onClose: onCloseEditDialog,
  } = useDisclosure();
  const {
    isOpen: isOpenDeleteDialog,
    onOpen: onOpenDeleteDialog,
    onClose: onCloseDeleteDialog,
  } = useDisclosure();

  const selectedTaskId = current?.task_id;
  const selectedFiles = current?.selectedFiles;
  const isUploadingFiles = status === UPLOAD_FILES_STATUS.UPLOADING;
  const isWaitingUpdateFileUploadStatus = selectedFiles.some((file) =>
    [TRANSLATION_FILE_STATUS.UPLOADED, TRANSLATION_FILE_STATUS.IDLE].includes(
      file.translation_file_status
    )
  );
  const isChangingSelectedFiles =
    JSON.stringify(current?.selectedFiles) !== JSON.stringify(current?.initialSelectedFiles);

  const isBlockingAddNewTask =
    data?.[0]?.type === TEMPORARY || isWaitingUpdateFileUploadStatus || isUploadingFiles;

  const onShowMore = () => {
    dispatch(
      translationAction.getTranslationTasks({
        page: current_page + 1,
        size: 10,
      })
    );
  };

  const onClickDelete = (task: TranslationTask) => {
    setRecordDelete(task);
    onOpenDeleteDialog();
  };

  const onClickEdit = (task: TranslationTask) => {
    setRecordEdit(task);
    onOpenEditDialog();
  };

  const handleDelete = (task: TranslationTask) => {
    dispatch(
      translationAction.deleteTranslationTask({
        id: task.id,
      })
    );
  };

  const handleEdit = (updateTask: TranslationTask & { new_name: string }) => {
    dispatch(
      translationAction.editTranslationTaskName({
        id: updateTask.id,
        new_name: updateTask.new_name,
      })
    );
  };

  const onChangeTask = (task: Task) => {
    // Block change task when waiting file upload status updated
    if (isWaitingUpdateFileUploadStatus || isUploadingFiles) {
      return;
    }
    if (isChangingSelectedFiles && current.type !== TEMPORARY && current.task_id !== task.id) {
      onOpenWarningChangeTaskDialog();
      setSelectTask(task);
    } else {
      onContinueChangeTask(task);
    }
  };

  const onContinueChangeTask = (task: Task) => {
    onChange(task);
  };

  const onClickToggleSideBar = () => {
    setIsToggleSidebar(!isToggleSidebar);
  };

  useEffect(() => {
    if (selectedTaskId) {
      dispatch(translationAction.getDetailTranslationTask(current));
    }
  }, [selectedTaskId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <RouterPrompt when={isChangingSelectedFiles} />
      <DeleteModal
        isOpen={isOpenDeleteDialog}
        onClose={onCloseDeleteDialog}
        recordDelete={recordDelete}
        action={handleDelete}
        dataHeader={t('translator.deleteTask')}
        dataBody={t('translator.deleteTaskInstruction')}
        btnAction={t('common.delete')}
        color='red'
      />
      <EditModal
        isOpen={isOpenEditDialog}
        onClose={onCloseEditDialog}
        recordEdit={recordEdit}
        action={handleEdit}
        dataHeader={t('translator.renameTask')}
        btnAction={t('translator.rename')}
      />
      <Box
        w={isToggleSidebar ? '100%' : '0px'}
        maxW={'320px'}
        h='calc(100vh - 175px)'
        bg='bg_main_container'
        borderRadius='20px'
        transition='width .05s ease-in-out'
      >
        <Box
          overflow={isToggleSidebar ? 'auto' : 'hidden'}
          w={isToggleSidebar ? '100%' : '0px'}
          maxH='calc(100% - 100px)'
          visibility={isToggleSidebar ? 'visible' : 'hidden'}
          transition='width .05s ease-in-out'
        >
          <Flex align='center' mt={2} pt={2}>
            <Heading as='h3' mr='1rem' color={'white.600'} fontWeight={'bold'} size='md'>
              {t('translator.translationJobs')}
            </Heading>
            <Spacer />
            <Tooltip label={t('common.addNew')}>
              <Button
                pos='relative'
                w='40px'
                h='40px'
                bg='grey.800'
                borderRadius='50%'
                _hover={{}}
                _focus={{}}
                cursor='pointer'
                isDisabled={isBlockingAddNewTask}
                onClick={onAddNew}
              >
                <BiPlusMedical
                  color='white'
                  style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    zIndex: '999',
                  }}
                />
                <Box pos='absolute' w='40px' h='40px' bg='main_summary_color' borderRadius='50%' />
              </Button>
            </Tooltip>
          </Flex>

          <Box mt={5} mb={3} fontSize={12}>
            {t('common.recent')}
          </Box>
          <Box>
            {data?.map((item) => (
              <Box
                key={item?.id}
                pos='relative'
                h='60px'
                my={2}
                p='12px 14px 20px 20px'
                fontSize='14px'
                fontWeight={selectedTaskId === item?.id ? '600' : ''}
                textAlign='left'
                bg={
                  selectedTaskId === item?.id
                    ? 'bg_conversation_item_select'
                    : 'bg_conversation_item'
                }
                borderRadius='10px'
                shadow={
                  selectedTaskId === item?.id ? 'shadow_box' : 'rgba(0, 0, 0, 0.05) 0px 0px 0px 1px'
                }
                _hover={{
                  bg: 'bg_conversation_item_select',
                  boxShadow: 'shadow_box',
                }}
                cursor='pointer'
                onClick={() => onChangeTask(item)}
              >
                <Flex align='center' justify='between'>
                  <Box w='20px'>
                    <BsChatLeftDots size={20} />
                  </Box>
                  <Tooltip label={item.name} placement='top-start'>
                    <Box
                      overflow='hidden'
                      maxW='160px'
                      ms='12px'
                      whiteSpace='nowrap'
                      textOverflow='ellipsis'
                    >
                      {item?.name}
                    </Box>
                  </Tooltip>
                  <Spacer />
                  <Box w='20px'>
                    <TaskOptions
                      data={item}
                      onClickDelete={onClickDelete}
                      onClickEdit={onClickEdit}
                    />
                  </Box>
                </Flex>
                <Box pos='absolute' right='42px' bottom='4px' fontSize='12px'>
                  {timeFormat(item?.last_updated)}
                </Box>
              </Box>
            ))}
          </Box>

          {current_page + 1 < total_page && (
            <Box
              fontSize='12px'
              textAlign='right'
              textDecoration='underline'
              cursor='pointer'
              onClick={onShowMore}
            >
              {t('common.moreHistory')}
            </Box>
          )}
        </Box>
        <Tooltip label={isToggleSidebar ? t('common.close') : t('common.open')} placement='right'>
          <Button
            pos='absolute'
            bottom={10}
            left={24}
            color='grey.50'
            onClick={onClickToggleSideBar}
            variant='submit_summary'
          >
            <PiSidebarSimpleBold />
          </Button>
        </Tooltip>
      </Box>
    </>
  );
};

interface TaskOptionsProps {
  data: TranslationTask;
  onClickDelete: (_data: TranslationTask) => void;
  onClickEdit: (_data: TranslationTask) => void;
}

const TaskOptions = ({ data, onClickDelete, onClickEdit }: TaskOptionsProps) => {
  const { t } = useTranslation();
  return (
    <Menu>
      <MenuButton onClick={(e) => e.stopPropagation()}>
        <BsThreeDotsVertical />
      </MenuButton>
      <MenuList minW='100px' p={0} bg='transparent' borderRadius={0}>
        {data?.type !== TEMPORARY && (
          <MenuItem
            bg='menu_item_option'
            _hover={{ bg: 'menu_item_option_hover' }}
            onClick={(e) => {
              e.stopPropagation();
              onClickEdit(data);
            }}
          >
            {t('common.edit')}
          </MenuItem>
        )}
        <MenuItem
          bg='menu_item_option'
          _hover={{ bg: 'menu_item_option_hover' }}
          onClick={(e) => {
            e.stopPropagation();
            onClickDelete(data);
          }}
        >
          {t('common.delete')}
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export default TranslationJobs;
